import {
  normalizeWidgetTheme,
  APIURL,
  cancelSelectEvent,
  changeSymbolForWidget,
  getCSSStyles,
  getSymbolDropdown,
  getTraderSMCardHTML,
  getWidgetDataURI,
  apiGet,
  toggleWidgetTheme,
  getEChartsTooltipTheme,
  getHeaderPinStateClass,
  getHeaderPinButtonHTML,
  setupHeaderPin
} from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

type StatementType = 'balance_sheet' | 'income_statement' | 'cash_flow';
type PeriodType = 'annual' | 'quarterly';
type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

interface PeriodRow {
  date: string;
  fiscal_year: number;
  quarter?: string;
  [key: string]: string | number | null | undefined;
}

interface IncomeStatementFlowApiData {
  symbol: string;
  country: string;
  statement_type: StatementType;
  period_type: PeriodType;
  metric_1: string;
  metric_2: string;
  periods: number;
  show_metric1_pct_change: boolean;
  show_metric2_pct_change: boolean;
  data: PeriodRow[];
}

interface ProductSegmentItem {
  category: string;
  value: number;
  display_value?: string;
}

interface ProductSegmentsData {
  pie_data: ProductSegmentItem[];
  total_revenue: number;
}

interface SankeyChartNode {
  name: string;
  depth?: number;
  itemStyle?: { color: string };
}

interface SankeyChartLink {
  source: string;
  target: string;
  value: number;
  lineStyle?: { color?: string; opacity?: number };
}

interface IncomeStatementFlowConfig extends WidgetConfig {
  statementType?: StatementType;
  periodType?: PeriodType;
  primaryMetric?: string;
  secondaryMetric?: string;
  limit?: number;
}

const STATEMENT_OPTIONS: { value: StatementType; label: string }[] = [
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'income_statement', label: 'Income Statement' },
  { value: 'cash_flow', label: 'Cash Flow' }
];

const METRICS_BY_STATEMENT: Record<StatementType, { value: string; label: string }[]> = {
  balance_sheet: [
    { value: 'total_assets', label: 'Total Assets' },
    { value: 'total_liabilities', label: 'Total Liabilities' },
    { value: 'total_stockholders_equity', label: 'Total Stockholders Equity' },
    { value: 'cash_and_cash_equivalents', label: 'Cash & Equivalents' },
    { value: 'total_debt', label: 'Total Debt' },
    { value: 'net_debt', label: 'Net Debt' }
  ],
  income_statement: [
    { value: 'revenue', label: 'Revenue' },
    { value: 'net_income', label: 'Net Income' },
    { value: 'gross_profit', label: 'Gross Profit' },
    { value: 'operating_income', label: 'Operating Income' },
    { value: 'ebitda', label: 'EBITDA' },
    { value: 'eps', label: 'EPS' }
  ],
  cash_flow: [
    { value: 'operating_cash_flow', label: 'Operating Cash Flow' },
    { value: 'free_cash_flow', label: 'Free Cash Flow' },
    { value: 'net_cash_provided_by_operating_activities', label: 'Net Cash from Operations' },
    { value: 'capital_expenditure', label: 'Capital Expenditure' }
  ]
};

const DEFAULT_METRICS: Record<StatementType, { primary: string; secondary: string }> = {
  balance_sheet: { primary: 'total_assets', secondary: 'total_liabilities' },
  income_statement: { primary: 'revenue', secondary: 'net_income' },
  cash_flow: { primary: 'operating_cash_flow', secondary: 'free_cash_flow' }
};

const LIMIT_OPTIONS = [3, 5, 10];

const PERIOD_COLORS = [
  '#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#6366F1'
];

const SEGMENT_COLORS: Record<string, string> = {
  iPhone: '#3B82F6',
  Service: '#D97706',
  'Wearables, Home and Accessories': '#10B981',
  Wearables: '#10B981',
  Mac: '#EF4444',
  iPad: '#8B5CF6'
};

const NODE_COLORS: Record<string, string> = {
  'Total Revenue': '#60A5FA',
  'Cost of Revenues': '#B91C1C',
  'Gross Profit': '#10B981',
  'Operating Income': '#8B5CF6',
  'R&D': '#EAB308',
  'SG&A': '#F97316',
  'Income Before Tax': '#22C55E',
  'Net Income': '#3B82F6',
  Tax: '#EF4444',
  'Interest Expense': '#DC2626',
  'Other Income': '#F87171',
  'Total Assets': '#3B82F6',
  'Total Liabilities': '#EF4444',
  'Total Stockholders Equity': '#10B981'
};

const INCOME_FLOW_BATCHES: Array<{ primary: string; secondary: string }> = [
  { primary: 'revenue', secondary: 'cost_of_revenue' },
  { primary: 'gross_profit', secondary: 'operating_income' },
  { primary: 'research_and_development_expenses', secondary: 'selling_general_and_administrative_expenses' },
  { primary: 'income_before_tax', secondary: 'net_income' },
  { primary: 'income_tax_expense', secondary: 'interest_expense' },
  { primary: 'total_other_income_expenses_net', secondary: 'operating_expenses' }
];

export class TraderverseIncomeStatementFlow {
  private container: HTMLElement;
  private config: IncomeStatementFlowConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private currentData: IncomeStatementFlowApiData | null = null;
  private flowMetricsByPeriod: Map<string, Record<string, number>> = new Map();
  private segmentsByPeriod: Map<string, ProductSegmentItem[]> = new Map();
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
  private selectedPeriodIndex = 0;
  private filters = {
    statementType: 'income_statement' as StatementType,
    periodType: 'annual' as PeriodType,
    primaryMetric: 'revenue',
    secondaryMetric: 'net_income',
    limit: 10
  };

  constructor(config: IncomeStatementFlowConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.applyConfigFilters();
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<IncomeStatementFlowConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      isSymbolSelectable: true,
      statementType: 'income_statement',
      periodType: 'annual',
      primaryMetric: 'revenue',
      secondaryMetric: 'net_income',
      limit: 10
    };
  }

  private applyConfigFilters(): void {
    if (this.config.statementType) this.filters.statementType = this.config.statementType;
    if (this.config.periodType) this.filters.periodType = this.config.periodType;
    if (this.config.primaryMetric) this.filters.primaryMetric = this.config.primaryMetric;
    if (this.config.secondaryMetric) this.filters.secondaryMetric = this.config.secondaryMetric;
    if (this.config.limit) this.filters.limit = this.config.limit;
    this.ensureValidMetrics();
  }

  private ensureValidMetrics(): void {
    const options = METRICS_BY_STATEMENT[this.filters.statementType];
    const defaults = DEFAULT_METRICS[this.filters.statementType];
    if (!options.some(o => o.value === this.filters.primaryMetric)) {
      this.filters.primaryMetric = defaults.primary;
    }
    if (!options.some(o => o.value === this.filters.secondaryMetric)) {
      this.filters.secondaryMetric = defaults.secondary;
    }
    if (this.filters.primaryMetric === this.filters.secondaryMetric) {
      const alt = options.find(o => o.value !== this.filters.primaryMetric);
      if (alt) this.filters.secondaryMetric = alt.value;
    }
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private renderMetricOptions(selected: string): string {
    return METRICS_BY_STATEMENT[this.filters.statementType]
      .map(metric => `<option value="${metric.value}"${metric.value === selected ? ' selected' : ''}>${metric.label}</option>`)
      .join('');
  }

  private getPeriodKey(row: PeriodRow): string {
    if (this.filters.periodType === 'quarterly' && row.quarter) {
      return `${row.fiscal_year}-${row.quarter}`;
    }
    return String(row.fiscal_year);
  }

  private formatPeriodLabel(row: PeriodRow): string {
    if (this.filters.periodType === 'quarterly' && row.quarter) {
      return `${row.fiscal_year} ${row.quarter}`;
    }
    return String(row.fiscal_year);
  }

  private getMetricLabel(metric: string): string {
    const all = Object.values(METRICS_BY_STATEMENT).flat();
    return all.find(item => item.value === metric)?.label || this.formatMetricKey(metric);
  }

  private formatMetricKey(key: string): string {
    return key.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  private renderPeriodLegendHtml(): string {
    const rows = this.currentData?.data || [];
    if (!rows.length) return '';
    return rows.map((row, index) => {
      const active = index === this.selectedPeriodIndex ? ' active' : '';
      const color = PERIOD_COLORS[index % PERIOD_COLORS.length];
      const label = this.formatPeriodLabel(row);
      return `<span class="toggle-series${active}" data-period-index="${index}" data-tooltip="${label}" style="background-color:${color};"></span>`;
    }).join('');
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.attachStyles();
    this.setupEventListeners();
    await this.fetchData(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} Income Statement Flow</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
            </div>
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>Statement Type</label>
                <select class="statement-type-select dropdown-btn">
                  ${STATEMENT_OPTIONS.map(option => `<option value="${option.value}"${option.value === this.filters.statementType ? ' selected' : ''}>${option.label}</option>`).join('')}
                </select>
              </div>
              <div class="item">
                <label>Period Type</label>
                <select class="period-type-select dropdown-btn">
                  <option value="annual"${this.filters.periodType === 'annual' ? ' selected' : ''}>Annual</option>
                  <option value="quarterly"${this.filters.periodType === 'quarterly' ? ' selected' : ''}>Quarterly</option>
                </select>
              </div>
              <div class="item">
                <label>Primary Metric</label>
                <select class="primary-metric-select dropdown-btn">${this.renderMetricOptions(this.filters.primaryMetric)}</select>
              </div>
              <div class="item">
                <label>Secondary Metric</label>
                <select class="secondary-metric-select dropdown-btn">${this.renderMetricOptions(this.filters.secondaryMetric)}</select>
              </div>
              <div class="item">
                <label>Periods</label>
                <select class="limit-select dropdown-btn">
                  ${LIMIT_OPTIONS.map(limit => `<option value="${limit}"${limit === this.filters.limit ? ' selected' : ''}>${limit}</option>`).join('')}
                </select>
              </div>
            </div>
            <div class="item">
              <div class="legendsCollection" id="period-legends">${this.renderPeriodLegendHtml()}</div>
            </div>
          </div>
          <div class="chartDv" data-testing-id="chart-container">
            <div class="cashDebtMain"><div id="incomeStatementFlowChart" class="chart"></div></div>
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }

    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
    }

    this.attachPeriodLegendHandlers();
  }

  private refreshMetricDropdowns(): void {
    const primarySelect = this.shadowRoot.querySelector('.primary-metric-select') as HTMLSelectElement;
    const secondarySelect = this.shadowRoot.querySelector('.secondary-metric-select') as HTMLSelectElement;
    if (primarySelect) primarySelect.innerHTML = this.renderMetricOptions(this.filters.primaryMetric);
    if (secondarySelect) secondarySelect.innerHTML = this.renderMetricOptions(this.filters.secondaryMetric);
  }

  private refreshPeriodLegends(): void {
    const legendContainer = this.shadowRoot.querySelector('#period-legends');
    if (!legendContainer) return;
    legendContainer.innerHTML = this.renderPeriodLegendHtml();
    this.attachPeriodLegendHandlers();
  }

  private attachPeriodLegendHandlers(): void {
    const legendSpans = this.shadowRoot.querySelectorAll('#period-legends .toggle-series');
    legendSpans.forEach(legend => {
      legend.addEventListener('click', () => {
        const index = Number((legend as HTMLElement).dataset.periodIndex);
        if (!Number.isFinite(index)) return;
        this.selectedPeriodIndex = index;
        legendSpans.forEach(el => el.classList.remove('active'));
        legend.classList.add('active');
        this.updateDisplay();
      });
    });
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

    if (filtersBox) {
      filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
      this.outsideClickHandler = (ev: MouseEvent) => {
        const anyEv: any = ev;
        const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
        const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
        if (!clickedInside && !filtersBox.classList.contains('hidden')) {
          filtersBox.classList.add('hidden');
        }
      };
      document.addEventListener('click', this.outsideClickHandler);
    }

    const statementTypeSelect = this.shadowRoot.querySelector('.statement-type-select') as HTMLSelectElement;
    const periodTypeSelect = this.shadowRoot.querySelector('.period-type-select') as HTMLSelectElement;
    const primaryMetricSelect = this.shadowRoot.querySelector('.primary-metric-select') as HTMLSelectElement;
    const secondaryMetricSelect = this.shadowRoot.querySelector('.secondary-metric-select') as HTMLSelectElement;
    const limitSelect = this.shadowRoot.querySelector('.limit-select') as HTMLSelectElement;

    const applyFiltersAndFetch = () => {
      this.filters.statementType = (statementTypeSelect?.value || this.filters.statementType) as StatementType;
      this.filters.periodType = (periodTypeSelect?.value || this.filters.periodType) as PeriodType;
      this.filters.primaryMetric = primaryMetricSelect?.value || this.filters.primaryMetric;
      this.filters.secondaryMetric = secondaryMetricSelect?.value || this.filters.secondaryMetric;
      this.filters.limit = Number(limitSelect?.value || this.filters.limit);
      this.ensureValidMetrics();
      this.syncConfigFromFilters();
      this.refreshMetricDropdowns();
      this.selectedPeriodIndex = 0;
      this.fetchData(this.config.symbol!);
    };

    statementTypeSelect?.addEventListener('change', () => {
      this.filters.statementType = statementTypeSelect.value as StatementType;
      const defaults = DEFAULT_METRICS[this.filters.statementType];
      this.filters.primaryMetric = defaults.primary;
      this.filters.secondaryMetric = defaults.secondary;
      this.refreshMetricDropdowns();
      applyFiltersAndFetch();
    });
    periodTypeSelect?.addEventListener('change', applyFiltersAndFetch);
    primaryMetricSelect?.addEventListener('change', applyFiltersAndFetch);
    secondaryMetricSelect?.addEventListener('change', applyFiltersAndFetch);
    limitSelect?.addEventListener('change', applyFiltersAndFetch);

    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;
  }

  private syncConfigFromFilters(): void {
    this.config.statementType = this.filters.statementType;
    this.config.periodType = this.filters.periodType;
    this.config.primaryMetric = this.filters.primaryMetric;
    this.config.secondaryMetric = this.filters.secondaryMetric;
    this.config.limit = this.filters.limit;
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async fetchFlowBatch(
    symbol: string,
    primaryMetric: string,
    secondaryMetric: string
  ): Promise<PeriodRow[]> {
    const response = await apiGet({
      method: 'GET',
      url: `${APIURL}/v1/symbol-specific/income-statement-flow`,
      query: {
        symbol,
        statementType: this.filters.statementType,
        periodType: this.filters.periodType,
        primaryMetric,
        secondaryMetric,
        limit: this.filters.limit
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) return [];
    const json = await response.json();
    if (!json?.isSuccess || !json.data?.data) return [];
    return json.data.data as PeriodRow[];
  }

  private mergeFlowMetrics(rows: PeriodRow[]): void {
    rows.forEach(row => {
      const key = this.getPeriodKey(row);
      const existing = this.flowMetricsByPeriod.get(key) || {};
      Object.keys(row).forEach(field => {
        const value = row[field];
        if (typeof value === 'number' && Number.isFinite(value)) {
          existing[field] = value;
        }
      });
      this.flowMetricsByPeriod.set(key, existing);
    });
  }

  private async fetchFlowMetrics(symbol: string): Promise<void> {
    this.flowMetricsByPeriod.clear();
    this.segmentsByPeriod.clear();

    if (this.filters.statementType === 'income_statement') {
      const batches = await Promise.all(
        INCOME_FLOW_BATCHES.map(batch => this.fetchFlowBatch(symbol, batch.primary, batch.secondary))
      );
      batches.forEach(rows => this.mergeFlowMetrics(rows));

      const rows = this.currentData?.data || [];
      await Promise.all(rows.map(row => this.fetchProductSegments(symbol, row)));
      return;
    }

    if (this.filters.statementType === 'balance_sheet') {
      const rows = await this.fetchFlowBatch(symbol, 'total_assets', 'total_liabilities');
      this.mergeFlowMetrics(rows);
      const equityRows = await this.fetchFlowBatch(symbol, 'total_stockholders_equity', 'total_assets');
      this.mergeFlowMetrics(equityRows);
    }
  }

  private async fetchProductSegments(symbol: string, row: PeriodRow): Promise<void> {
    const key = this.getPeriodKey(row);
    if (this.segmentsByPeriod.has(key)) return;

    try {
      if (this.filters.periodType === 'quarterly' && row.quarter) {
        const segments = await this.fetchSegmentsForQuarter(symbol, row.fiscal_year, row.quarter as Quarter);
        this.segmentsByPeriod.set(key, segments);
        return;
      }

      const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
      const results = await Promise.all(
        quarters.map(quarter => this.fetchSegmentsForQuarter(symbol, row.fiscal_year, quarter))
      );
      const aggregated = new Map<string, number>();
      results.flat().forEach(segment => {
        aggregated.set(segment.category, (aggregated.get(segment.category) || 0) + segment.value);
      });
      const segments = Array.from(aggregated.entries()).map(([category, value]) => ({ category, value }));
      this.segmentsByPeriod.set(key, segments);
    } catch {
      this.segmentsByPeriod.set(key, []);
    }
  }

  private async fetchSegmentsForQuarter(
    symbol: string,
    year: number,
    quarter: Quarter
  ): Promise<ProductSegmentItem[]> {
    const response = await apiGet({
      method: 'GET',
      url: `${APIURL}/v1/symbol-specific/revenue-product-segments`,
      query: { symbol, year, quarter },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) return [];
    const json = await response.json();
    if (!json?.isSuccess || !json.data?.pie_data) return [];
    return (json.data as ProductSegmentsData).pie_data;
  }

  private async fetchData(symbol: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');

    try {
      if ((this.config as any).data) {
        this.currentData = (this.config as any).data as IncomeStatementFlowApiData;
        await this.fetchFlowMetrics(symbol);
        this.refreshPeriodLegends();
        this.updateDisplay();
        this.emit('dataUpdate', this.currentData);
        return;
      }

      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/symbol-specific/income-statement-flow`,
        query: {
          symbol,
          statementType: this.filters.statementType,
          periodType: this.filters.periodType,
          primaryMetric: this.filters.primaryMetric,
          secondaryMetric: this.filters.secondaryMetric,
          limit: this.filters.limit
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const json = await response.json();
      if (!json?.isSuccess) throw new Error(json?.message || 'Failed to fetch income statement flow');

      this.currentData = json.data as IncomeStatementFlowApiData;
      if (!this.currentData?.data?.length) {
        this.currentData = null;
        this.flowMetricsByPeriod.clear();
        this.segmentsByPeriod.clear();
        this.updateDisplay();
        this.emit('dataUpdate', { symbol, ...this.filters, data: null });
        return;
      }

      if (this.selectedPeriodIndex >= this.currentData.data.length) {
        this.selectedPeriodIndex = 0;
      }

      this.filters.primaryMetric = this.currentData.metric_1 || this.filters.primaryMetric;
      this.filters.secondaryMetric = this.currentData.metric_2 || this.filters.secondaryMetric;
      this.syncConfigFromFilters();
      this.refreshMetricDropdowns();

      await this.fetchFlowMetrics(symbol);
      this.refreshPeriodLegends();
      this.updateDisplay();
      this.emit('dataUpdate', {
        symbol,
        ...this.filters,
        data: this.currentData
      });
    } catch (err) {
      console.error('Error fetching chart data:', err);
      this.currentData = null;
      this.flowMetricsByPeriod.clear();
      this.segmentsByPeriod.clear();
      this.updateDisplay();
      this.emit('error', err);
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private getSelectedPeriodRow(): PeriodRow | null {
    if (!this.currentData?.data?.length) return null;
    return this.currentData.data[this.selectedPeriodIndex] || this.currentData.data[0];
  }

  private getFlowValue(metrics: Record<string, number>, key: string): number {
    const value = metrics[key];
    return Number.isFinite(value) ? Math.abs(value) : 0;
  }

  private formatBillions(value: number): string {
    if (!Number.isFinite(value) || value === 0) return '$0';
    const billions = value / 1e9;
    if (billions >= 1) return `$${billions.toFixed(2)}B`;
    const millions = value / 1e6;
    return `$${millions.toFixed(2)}M`;
  }

  private scaleSegments(segments: ProductSegmentItem[], targetRevenue: number): ProductSegmentItem[] {
    if (!segments.length || !targetRevenue) return segments;
    const total = segments.reduce((sum, item) => sum + item.value, 0);
    if (!total) return segments;
    const factor = targetRevenue / total;
    return segments.map(segment => ({
      ...segment,
      value: segment.value * factor
    }));
  }

  private addLink(links: SankeyChartLink[], source: string, target: string, value: number, color?: string): void {
    if (!Number.isFinite(value) || value <= 0) return;
    links.push({
      source,
      target,
      value,
      lineStyle: { color: color || NODE_COLORS[source] || NODE_COLORS[target] || '#8B6CFF', opacity: 0.45 }
    });
  }

  private addNode(nodes: SankeyChartNode[], name: string, depth: number, color?: string): void {
    if (nodes.some(node => node.name === name)) return;
    nodes.push({
      name,
      depth,
      itemStyle: { color: color || NODE_COLORS[name] || SEGMENT_COLORS[name] || '#8B6CFF' }
    });
  }

  private buildIncomeStatementSankey(
    metrics: Record<string, number>,
    segments: ProductSegmentItem[]
  ): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } {
    const nodes: SankeyChartNode[] = [];
    const links: SankeyChartLink[] = [];

    const revenue = this.getFlowValue(metrics, 'revenue');
    const costOfRevenue = this.getFlowValue(metrics, 'cost_of_revenue');
    const grossProfit = this.getFlowValue(metrics, 'gross_profit') || Math.max(revenue - costOfRevenue, 0);
    const rd = this.getFlowValue(metrics, 'research_and_development_expenses');
    const sga = this.getFlowValue(metrics, 'selling_general_and_administrative_expenses');
    const operatingIncome = this.getFlowValue(metrics, 'operating_income') || Math.max(grossProfit - rd - sga, 0);
    const incomeBeforeTax = this.getFlowValue(metrics, 'income_before_tax') || operatingIncome;
    const incomeTax = this.getFlowValue(metrics, 'income_tax_expense');
    const netIncome = this.getFlowValue(metrics, 'net_income');
    const interestExpense = this.getFlowValue(metrics, 'interest_expense');
    const otherIncome = Math.abs(metrics.total_other_income_expenses_net || 0);

    const scaledSegments = this.scaleSegments(segments, revenue);
    if (scaledSegments.length) {
      scaledSegments.forEach(segment => {
        const label = segment.category;
        this.addNode(nodes, label, 0, SEGMENT_COLORS[label]);
        this.addLink(links, label, 'Total Revenue', segment.value, SEGMENT_COLORS[label]);
      });
    } else if (revenue > 0) {
      this.addNode(nodes, 'Revenue', 0, NODE_COLORS['Total Revenue']);
      this.addLink(links, 'Revenue', 'Total Revenue', revenue, NODE_COLORS['Total Revenue']);
    }

    this.addNode(nodes, 'Total Revenue', 1, NODE_COLORS['Total Revenue']);
    this.addNode(nodes, 'Cost of Revenues', 2, NODE_COLORS['Cost of Revenues']);
    this.addNode(nodes, 'Gross Profit', 2, NODE_COLORS['Gross Profit']);
    this.addLink(links, 'Total Revenue', 'Cost of Revenues', costOfRevenue);
    this.addLink(links, 'Total Revenue', 'Gross Profit', grossProfit || Math.max(revenue - costOfRevenue, 0));

    this.addNode(nodes, 'R&D', 3, NODE_COLORS['R&D']);
    this.addNode(nodes, 'SG&A', 3, NODE_COLORS['SG&A']);
    this.addNode(nodes, 'Operating Income', 3, NODE_COLORS['Operating Income']);
    this.addLink(links, 'Gross Profit', 'R&D', rd);
    this.addLink(links, 'Gross Profit', 'SG&A', sga);
    this.addLink(links, 'Gross Profit', 'Operating Income', operatingIncome);

    this.addNode(nodes, 'Income Before Tax', 4, NODE_COLORS['Income Before Tax']);
    this.addLink(links, 'Operating Income', 'Income Before Tax', incomeBeforeTax);

    this.addNode(nodes, 'Net Income', 5, NODE_COLORS['Net Income']);
    this.addNode(nodes, 'Tax', 5, NODE_COLORS.Tax);
    this.addLink(links, 'Income Before Tax', 'Net Income', netIncome);
    this.addLink(links, 'Income Before Tax', 'Tax', incomeTax);

    if (interestExpense > 0) {
      this.addNode(nodes, 'Interest Expense', 5, NODE_COLORS['Interest Expense']);
      this.addLink(links, 'Income Before Tax', 'Interest Expense', interestExpense);
    }

    if (otherIncome > 0) {
      this.addNode(nodes, 'Other Income', 5, NODE_COLORS['Other Income']);
      this.addLink(links, 'Income Before Tax', 'Other Income', otherIncome);
    }

    return { nodes, links };
  }

  private buildBalanceSheetSankey(metrics: Record<string, number>): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } {
    const nodes: SankeyChartNode[] = [];
    const links: SankeyChartLink[] = [];

    const totalAssets = this.getFlowValue(metrics, 'total_assets');
    const totalLiabilities = this.getFlowValue(metrics, 'total_liabilities');
    const totalEquity = this.getFlowValue(metrics, 'total_stockholders_equity');

    this.addNode(nodes, 'Total Assets', 0, NODE_COLORS['Total Assets']);
    this.addNode(nodes, 'Total Liabilities', 1, NODE_COLORS['Total Liabilities']);
    this.addNode(nodes, 'Total Stockholders Equity', 1, NODE_COLORS['Total Stockholders Equity']);
    this.addLink(links, 'Total Assets', 'Total Liabilities', totalLiabilities);
    this.addLink(links, 'Total Assets', 'Total Stockholders Equity', totalEquity || Math.max(totalAssets - totalLiabilities, 0));

    return { nodes, links };
  }

  private buildChartPayload(): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } {
    const row = this.getSelectedPeriodRow();
    if (!row) return { nodes: [], links: [] };

    const metrics = this.flowMetricsByPeriod.get(this.getPeriodKey(row)) || {};
    Object.keys(row).forEach(field => {
      const value = row[field];
      if (typeof value === 'number' && Number.isFinite(value)) {
        metrics[field] = value;
      }
    });

    if (this.filters.statementType === 'balance_sheet') {
      return this.buildBalanceSheetSankey(metrics);
    }

    if (this.filters.statementType === 'income_statement') {
      const segments = this.segmentsByPeriod.get(this.getPeriodKey(row)) || [];
      return this.buildIncomeStatementSankey(metrics, segments);
    }

    return { nodes: [], links: [] };
  }

  private updateDisplay(): void {
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    if (this.currentData?.data?.length) {
      chartDv?.setAttribute('data-testing-id', 'income-statement-flow-chartdv');
    } else {
      chartDv?.removeAttribute('data-testing-id');
    }

    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#incomeStatementFlowChart') as HTMLElement;
    if (!container) return;

    if (this.currentChart) {
      try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
      this.currentChart = null;
    }

    if (!this.currentData?.data?.length) {
      container.innerHTML = '<div class="noData"><p>No income statement flow data available.</p></div>';
      return;
    }

    const { nodes, links } = this.buildChartPayload();
    if (!links.length) {
      const message = this.filters.statementType === 'cash_flow'
        ? 'Cash flow data is not available for this symbol.'
        : 'No flow data available for the selected period.';
      container.innerHTML = `<div class="noData"><p>${message}</p></div>`;
      return;
    }

    container.innerHTML = '';
    const row = this.getSelectedPeriodRow();
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim();
    const labelColor = getComputedStyle(widgetBox).getPropertyValue('--text-heading').trim() || textColor;
    const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm').trim();
    const bgPrimary = getComputedStyle(widgetBox).getPropertyValue('--bg-primary').trim();
    const tooltipTheme = getEChartsTooltipTheme(widgetBox);

    const chart = echarts.init(container, null, { backgroundColor: bgPrimary });
    chart.setOption({
      backgroundColor: 'transparent',
      animation: false,
      tooltip: {
        ...tooltipTheme,
        trigger: 'item',
        triggerOn: 'mousemove',
        formatter: (params: any) => {
          if (params.dataType === 'edge') {
            return `<div><b>${params.data.source}</b> → <b>${params.data.target}</b><br/>${this.formatBillions(params.data.value)}</div>`;
          }
          const nodeValue = links
            .filter(link => link.source === params.name)
            .reduce((sum, link) => sum + link.value, 0);
          const inValue = links
            .filter(link => link.target === params.name)
            .reduce((sum, link) => sum + link.value, 0);
          const displayValue = Math.max(nodeValue, inValue);
          return `<div><b>${params.name}</b>${displayValue ? `<br/>${this.formatBillions(displayValue)}` : ''}</div>`;
        }
      },
      series: [{
        type: 'sankey',
        left: '2%',
        right: '14%',
        top: '4%',
        bottom: '8%',
        nodeWidth: 14,
        nodeGap: 12,
        layoutIterations: 32,
        emphasis: { focus: 'adjacency' },
        label: {
          color: labelColor,
          fontSize,
          formatter: (params: any) => params.name
        },
        lineStyle: { curveness: 0.5 },
        data: nodes,
        links
      }],
      title: row ? {
        text: this.formatPeriodLabel(row),
        left: 'center',
        top: 0,
        textStyle: { color: textColor, fontSize: 12, fontWeight: 500 }
      } : undefined
    });

    this.currentChart = chart;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.fetchData(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.currentChart) {
      try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
      this.currentChart = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach(h => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global { interface Window { TraderverseIncomeStatementFlow: typeof TraderverseIncomeStatementFlow; } }
if (typeof window !== 'undefined') { window.TraderverseIncomeStatementFlow = TraderverseIncomeStatementFlow; }
